import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class RefreshTokenInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next
      .handle(request)
      .pipe(catchError((error) => this.refreshToken(error, request, next)));
  }

  private refreshToken(
    response: HttpErrorResponse,
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Only a 401 means the access token expired. Status 0 is a network/CORS
    // failure (Safari reports these often, e.g. after the tab was in the
    // background) and must not be treated as a session problem.
    if (response.status === 401) {
      // A failed refresh is handled (logout or not) by AuthService itself.
      if (this.isRefreshRequest(request)) {
        return throwError(() => response);
      }

      // Only the merchant/admin session can be refreshed here. Public,
      // unauthenticated surfaces (e.g. SMS tracking-link pages) have no
      // refresh token — let their original error pass through instead of
      // attempting (and failing) this refresh flow.
      if (!this.authService.canRefreshToken()) {
        return throwError(() => response);
      }

      // try to refresh the token (shared with any refresh already in flight)
      return this.authService.refreshTokenAsync().pipe(
        switchMap((token) => {
          request = request.clone({
            headers: request.headers.set(
              'Authorization',
              `Bearer ${token.access_token}`,
            ),
          });
          return next.handle(request);
        }),
        catchError(() => throwError(() => response)),
      );
    }
    return throwError(response);
  }

  private isRefreshRequest(request: HttpRequest<unknown>): boolean {
    return (
      request.url.includes('auth/refresh-token') ||
      (request.url.includes('auth/token') &&
        (request.body as Record<string, unknown>)?.grant_type ==
          'refresh_token')
    );
  }
}
